import React, { useEffect, useRef, useState, useCallback } from "react";
import useToast from "../../Toast";
import { CopyIcon } from "../../../utils/svg";
import { formatDate, formatTime, axiosInstance, authHeaders } from "../../../utils/helper";
import { OrderStatusList, TopupStatusList } from "../../../utils/constants";
import { useSelector, useDispatch } from "react-redux";
import { ClockIcon } from "@heroicons/react/24/outline";
import { clearPayment } from "../../../store/reducers/payment";
import useNavigation from "../../../hook/useNavigation";

const DetailTransaction = ({ orderDetails = {} }) => {
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const tab = useSelector((store) => store?.order?.tab);
  const navigate = useNavigation();

  const [timeLeft, setTimeLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const intervalRef = useRef(null);
  const isFetchingRef = useRef(false);

  const isWaitingPayment = orderDetails?.status_name === "Waiting Payment";

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchPaymentStatus = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      const data = new FormData();
      data.append("session_token", null);
      data.append("paymentReference", orderDetails?.payment_order_id ?? null);

      const res = await axiosInstance.post("status-payment", data, {
        headers: { "Content-Type": "multipart/form-data", ...authHeaders().headers }
      });

      const result = res?.data?.data ?? {};

      if (result?.status === "SUCCESS") {
        stopInterval();
        dispatch(clearPayment());
        navigate.push({
          pathname: "/payment/success",
          state: { payment: result }
        });
        return;
      }

      if (result?.is_expired) {
        stopInterval();
        setTimeLeft(0);
        setIsExpired(true);
        return;
      }

      if (result?.expired_at) {
        const diff = Math.floor(
          (new Date(result.expired_at).getTime() - Date.now()) / 1000
        );
        setTimeLeft(diff > 0 ? diff : 0);
        if (diff <= 0) {
          stopInterval();
          setIsExpired(true);
        }
      }
    } catch (error) {
      console.error("fetchPaymentStatus error:", error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [orderDetails?.payment_order_id, dispatch, navigate, stopInterval]);

  useEffect(() => {
    if (!isWaitingPayment) return;

    fetchPaymentStatus();

    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === null) return prev;

        if (prev <= 1) {
          stopInterval();
          setIsExpired(true);
          return 0;
        }

        if (prev % 10 === 0) {
          fetchPaymentStatus();
        }

        return prev - 1;
      });
    }, 1000);

    return () => stopInterval();
  }, [isWaitingPayment, orderDetails?.payment_order_id]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast("info", "Berhasil disalin");
    } catch {
      showToast("error", "Gagal menyalin");
    }
  };

  const statusDetails =
    tab === "pesanan"
      ? OrderStatusList?.[orderDetails?.orderStatus]
      : TopupStatusList?.[orderDetails?.topup_status];

  return (
    <div className="px-4">
      <h2 className="text-sm font-semibold">Detail Transaksi</h2>

      <div className="flex justify-between mt-4">
        <p className="text-xs font-normal">Nomor Pesanan</p>
        <div
          className="flex items-center cursor-pointer"
          onClick={() =>
            copyToClipboard(
              orderDetails?.orderNumber || orderDetails?.topup_order_number
            )
          }
        >
          <p className="text-xs font-semibold text-gray-900 pr-2">
            {orderDetails?.orderNumber || orderDetails?.topup_order_number}
          </p>
          <CopyIcon className="stroke-[var(--rose-violet)]" />
        </div>
      </div>

      <div className="flex justify-between mt-2">
        <p className="text-xs font-normal">Waktu Pembelian</p>
        <p className="text-xs font-semibold text-gray-900">
          {formatDate(
            orderDetails?.order_date || orderDetails?.created_at,
            "D MMM YYYY, HH:mm A"
          )}
        </p>
      </div>

      <div className="flex justify-between mt-2">
        <p className="text-xs font-normal">Status</p>
        <div className={`text-xs font-semibold ${statusDetails?.className}`}>
          {statusDetails?.message}
        </div>
      </div>

      {isWaitingPayment && (
        <div className="flex justify-between mt-2">
          <p className="text-xs font-normal">Batas pembayaran</p>
          {!isExpired ? (
            <div className="flex items-center text-red-500 font-semibold text-xs">
              <ClockIcon className="h-4 w-4 mr-1" />
              {formatTime(timeLeft)}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default DetailTransaction;
